import { LANGUAGES, type Language, type LangValues } from '../types';

export const LANGUAGE_LABELS: Record<Language, string> = {
  tr: 'Türkçe',
  en: 'İngilizce',
  fr: 'Fransızca',
  de: 'Almanca',
};

// "243 cm", "160x200 cm", "25 kg" gibi her dilde aynı olan ölçü değerleri
function isLanguageNeutral(text: string): boolean {
  return /\d/.test(text) && /^[\d\s.,x×%+\-/()]*(cm|mm|m|kg|gr|g)?\s*$/i.test(text.trim());
}

// Kaynak (TR) dolu ama seçili dilde çevirisi girilmemiş mi? (ölçüler çeviri gerektirmez)
export function isTranslationMissing(values: LangValues, lang: Language): boolean {
  const source = values.tr.trim();
  return lang !== 'tr' && source !== '' && values[lang].trim() === '' && !isLanguageNeutral(source);
}

interface LangInputProps {
  values: LangValues;
  lang: Language;
  onChange: (values: LangValues) => void;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  required?: boolean;
  className?: string;
  title?: string;
}

/**
 * Seçili dildeki değeri düzenleyen input/textarea.
 * Çeviri dillerinde Türkçe metin placeholder olarak gösterilir, eksik çeviriler vurgulanır.
 */
export function LangInput({
  values,
  lang,
  onChange,
  placeholder,
  multiline = false,
  rows,
  required = false,
  className,
  title,
}: LangInputProps) {
  const source = values.tr.trim();
  const isSource = lang === 'tr';
  const classes = [className, isTranslationMissing(values, lang) ? 'lang-input-missing' : '']
    .filter(Boolean)
    .join(' ');

  const props = {
    value: values[lang],
    onChange: (e: { target: { value: string } }) => onChange({ ...values, [lang]: e.target.value }),
    placeholder: isSource ? placeholder : source || placeholder,
    required: required && isSource,
    className: classes || undefined,
    title: isSource ? title : source ? `TR: ${source}` : title,
    lang,
  };

  return multiline ? <textarea {...props} rows={rows} /> : <input type="text" {...props} />;
}

interface LangTabsProps {
  active: Language;
  onChange: (lang: Language) => void;
  missingCounts: Record<Language, number>;
}

/**
 * Formun tamamında düzenlenen içerik dilini seçer.
 */
export function LangTabs({ active, onChange, missingCounts }: LangTabsProps) {
  return (
    <div className="lang-tabs">
      <span className="lang-tabs-label">İçerik dili:</span>
      {LANGUAGES.map((lang) => (
        <button
          key={lang}
          type="button"
          className={`lang-tab ${active === lang ? 'active' : ''}`}
          onClick={() => onChange(lang)}
          aria-pressed={active === lang}
          title={LANGUAGE_LABELS[lang]}
        >
          <span className="lang-tab-code">{lang.toUpperCase()}</span>
          <span className="lang-tab-name">{LANGUAGE_LABELS[lang]}</span>
          {lang !== 'tr' && (
            missingCounts[lang] > 0
              ? <span className="lang-tab-missing" title="Eksik çeviri">{missingCounts[lang]}</span>
              : <span className="lang-tab-done" title="Tüm çeviriler girildi">✓</span>
          )}
        </button>
      ))}
      {active !== 'tr' && (
        <span className="lang-tabs-hint">
          Soluk metinler Türkçe kaynağı gösterir. Boş bırakılan alanlarda
          {active === 'en' ? ' Türkçe' : ' İngilizce, o da yoksa Türkçe'} metin gösterilir.
        </span>
      )}
    </div>
  );
}
